import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";

import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import UpdateRoundedIcon from "@mui/icons-material/UpdateRounded";
import { ReactComponent as ExcelExportIcon } from "../assets/img/icons/excel_export.svg";
import * as XLSX from "xlsx";
import { SecureImage } from "./SecureImage";

export default function SalesDashboard() {
  const [currentTab, setCurrentTab] = useState("Active");
  const [loading, setLoading] = useState(false);
  const [ordersData, setOrdersData] = useState([]);
  const [isLogoutPopup, setIsLogoutPopup] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const navigate = useNavigate();

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

  // Fetch orders function
  const fetchOrderData = async () => {
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/api/fetchorder/getOrders?status=${
        currentTab === "Active" ? "Accept(WH)" : "Dispatched(SP)"
      }`;
      const { data } = await axios.get(url);

      const records = data?.data?.records || [];
      setOrdersData(records);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setOrdersData([]);
    setLoading(true);
    fetchOrderData();
  }, [currentTab]);

  // Group orders by order number
  const groupedByOrderNumber = useMemo(() => {
    if (!ordersData || ordersData.length === 0) return [];

    // Group orders by order_no
    const orderGroups = {};

    ordersData.forEach((order) => {
      const orderNo = order.order_no;

      if (!orderGroups[orderNo]) {
        orderGroups[orderNo] = {
          order_no: orderNo,
          orders: [],
          linkId: order.linkId,
          customerName: order.customerName,
          customerPhone: order.customerPhone,
          deliveryDate: order.deliveryDate,
          store_id: order.store_id,
        };
      }

      orderGroups[orderNo].orders.push(order);
    });

    // Convert to array
    return Object.values(orderGroups);
  }, [ordersData]);

  // Process each order group to handle sets
  const processOrderGroup = (orderGroup) => {
    const allItems = [];

    orderGroup.orders.forEach((order) => {
      if (
        order.selectedTagElements &&
        Array.isArray(order.selectedTagElements)
      ) {
        order.selectedTagElements.forEach((item) => {
          allItems.push({
            ...item,
            orderStatus: order.status,
            orderType: order.orderType,
            flag: order.flag || "i",
          });
        });
      }
    });

    // Group items into sets
    const orderFlag = allItems[0]?.flag || "i";

    if (orderFlag === "i") {
      return allItems.map((item) => ({
        type: "individual",
        parent: item,
        children: [],
      }));
    }

    const sets = [];
    const processedTags = new Set();

    allItems.forEach((item) => {
      if (processedTags.has(item.tagno)) return;

      const children = allItems.filter(
        (child) => child.crosstag === item.tagno && child.tagno !== item.tagno
      );

      if (children.length > 0) {
        sets.push({
          type: "set",
          parent: item,
          children: children,
        });

        processedTags.add(item.tagno);
        children.forEach((ch) => processedTags.add(ch.tagno));
      } else if (!allItems.some((parent) => parent.tagno === item.crosstag)) {
        sets.push({
          type: "individual",
          parent: item,
          children: [],
        });
        processedTags.add(item.tagno);
      }
    });

    return sets;
  };

  // Update status function - dispatches entire order
  const handleDispatchOrder = async (orderGroup) => {
    try {
      // Collect all tags from all orders in this order number group
      const allTags = [];

      orderGroup.orders.forEach((order) => {
        if (
          order.selectedTagElements &&
          Array.isArray(order.selectedTagElements)
        ) {
          order.selectedTagElements.forEach((item) => {
            allTags.push(item.tagno);
          });
        }
      });

      const payload = {
        linkId: orderGroup.linkId,
        orderNo: orderGroup.order_no,
        selectedTags: allTags,
        customerDetails: {
          customerName: orderGroup.customerName,
          customerPhone: orderGroup.customerPhone,
          deliveryDate: orderGroup.deliveryDate,
        },
        mfrId: orderGroup.store_id,
        status: "Dispatched(SP)",
        rejectedBy: "",
        rejectedReason: "",
      };

      await axios.post(
        `${API_BASE_URL}/api/fetchorder/changeOrderStatus`,
        payload
      );

      await fetchOrderData();
    } catch (err) {
      console.log(err);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    const prefix = localStorage.getItem("urlPrefix");
    if (prefix) {
      navigate(`/${prefix}`);
    } else {
      navigate("/login");
    }
  };

  // Excel export
  const exportGroupedToExcel = () => {
    if (!groupedByOrderNumber || groupedByOrderNumber.length === 0) {
      alert("No data available to export");
      return;
    }

    const exportRows = [];

    groupedByOrderNumber.forEach((orderGroup) => {
      const itemGroups = processOrderGroup(orderGroup);

      itemGroups.forEach((group) => {
        const allItems = [group.parent, ...group.children];

        allItems.forEach((item) => {
          exportRows.push({
            Order_No: orderGroup.order_no,
            Tag_No: item.tagno,
            Item: item.itemtype_name,
            Design: item.design_name,
            Gross: item.gross,
            Status: item.orderStatus || "",
            Customer_Name: orderGroup.customerName,
            Customer_Phone: orderGroup.customerPhone,
            Delivery_Date: orderGroup.deliveryDate,
          });
        });
      });
    });

    // Convert JSON to sheet
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

    // Excel file name
    const filename =
      currentTab === "Active" ? "Active_Orders.xlsx" : "Dispatched_Orders.xlsx";

    XLSX.writeFile(workbook, filename);
  };

  return (
    <Box sx={{ width: "100%", bgcolor: "#344758", minHeight: "100vh", p: 4 }}>
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          flexDirection: "column",
          gap: 2,
          mb: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontSize: isMobile ? 24 : 32,
              fontWeight: 600,
              mb: isMobile ? 2 : 4,
            }}
          >
            SALES DASHBOARD
          </Typography>
          <Box
            onClick={() => setIsLogoutPopup(true)}
            sx={{ display: "flex", gap: 1, cursor: "pointer" }}
          >
            <Typography sx={{ color: "#ffff" }}>Logout</Typography>
            <LogoutRoundedIcon sx={{ color: "#fff" }} />
          </Box>
        </Box>
      </Box>

      {/* Active / Dispatched Tabs & Excel Export Icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        {/* Active / Dispatched Tabs */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 2,
            mb: 1,
          }}
        >
          {/* Active Orders */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              borderRadius: 2,
              px: 1.5,
              py: 1,
              cursor: "pointer",
              bgcolor: currentTab === "Active" ? "#008085ff" : "#4e4e4eff",
            }}
            onClick={() => setCurrentTab("Active")}
          >
            <NotificationsActiveRoundedIcon
              sx={{
                fontSize: 22,
                fill: currentTab === "Active" ? "#FFF" : "#9e9e9eff",
              }}
            />
            <Typography
              sx={{
                fontSize: 18,
                color: currentTab === "Active" ? "#FFF" : "#9e9e9eff",
              }}
            >
              Active Orders
            </Typography>
          </Box>
          {/* History */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              borderRadius: 2,
              px: 1.5,
              py: 1,
              cursor: "pointer",
              bgcolor: currentTab === "History" ? "#008085ff" : "#4e4e4eff",
            }}
            onClick={() => setCurrentTab("History")}
          >
            <UpdateRoundedIcon
              sx={{
                fontSize: 22,
                fill: currentTab === "History" ? "#FFF" : "#9e9e9eff",
              }}
            />
            <Typography
              sx={{
                fontSize: 18,
                color: currentTab === "History" ? "#FFF" : "#9e9e9eff",
              }}
            >
              Dispatched
            </Typography>
          </Box>
        </Box>

        {/* Excel Export */}
        <Box>
          <IconButton
            onClick={exportGroupedToExcel}
            sx={{
              bgcolor: "#FFF",
              borderRadius: 2,
              width: 36,
              height: 36,
              "&:hover": {
                bgcolor: "#1c4b42ff",
                "& svg": {
                  fill: "#ffffff", // <-- icon color on hover
                },
              },
              "& svg": {
                transition: "0.2s",
                fill: "#42A996", // <-- default icon color
              },
            }}
            aria-label="Export Excel"
          >
            <ExcelExportIcon
              style={{
                width: 18,
                height: 18,
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* TABLE */}
      <Box
        sx={{
          bgcolor: "#2A3A4B",
          borderRadius: 2,
          overflowX: { xs: "auto", md: "visible" },
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          "& table": {
            width: "max-content",
            minWidth: "100%",
            tableLayout: "auto !important",
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "rgba(30,47,64,0.95)" }}>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Order No
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Tag No
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Item
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Design
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Gross
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Image
              </TableCell>
              {currentTab === "Active" && (
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                  Action
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {groupedByOrderNumber.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  sx={{ textAlign: "center", color: "#fff", py: 4 }}
                >
                  No orders found
                </TableCell>
              </TableRow>
            )}

            {groupedByOrderNumber.map((orderGroup, orderGroupIndex) => {
              const itemGroups = processOrderGroup(orderGroup);

              // Calculate total rows for this order
              const totalRows = itemGroups.reduce((sum, group) => {
                return sum + 1 + group.children.length;
              }, 0);

              let currentRowIndex = 0;

              return itemGroups.map((group, groupIndex) => {
                const isSet = group.type === "set";
                const allItems = [group.parent, ...group.children];
                const isFirstGroup = groupIndex === 0;
                const rowsInThisGroup = allItems.length;

                const row = (
                  <TableRow
                    key={`${orderGroupIndex}-${groupIndex}`}
                    sx={{
                      bgcolor:
                        orderGroupIndex % 2 === 0 ? "#1E2F40" : "#2A3A4B",
                      "& td": {
                        paddingY: 1,
                        paddingX: 0,
                        verticalAlign: "middle",
                      },
                    }}
                  >
                    {/* Order No - Only show for first group */}
                    {isFirstGroup && (
                      <TableCell
                        rowSpan={totalRows}
                        sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                      >
                        <Box
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                          }}
                        >
                          <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
                            {orderGroup.order_no}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}

                    {/* Tag column */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: index === 0 && isSet ? 600 : 400,
                              color: index === 0 && isSet ? "#4CAF50" : "#fff",
                            }}
                          >
                            {item.tagno}
                            {index === 0 && isSet && (
                              <Typography
                                component="span"
                                sx={{
                                  ml: 1,
                                  fontSize: 10,
                                  bgcolor: "#4CAF50",
                                  color: "#fff",
                                  px: 0.5,
                                  py: 0.2,
                                  borderRadius: 0.5,
                                }}
                              >
                                P
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                      ))}
                    </TableCell>

                    {/* Item */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                          }}
                        >
                          {item.itemtype_name}
                        </Box>
                      ))}
                    </TableCell>

                    {/* Design */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                          }}
                        >
                          {item.design_name}
                        </Box>
                      ))}
                    </TableCell>

                    {/* Gross */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                          }}
                        >
                          {parseFloat(item.gross).toFixed(3)}
                        </Box>
                      ))}
                    </TableCell>

                    {/* Image */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                          }}
                        >
                          {item.imageUrl ? (
                            <SecureImage
                              item={item}
                              width={80}
                              height={80}
                              borderRadius={2}
                            />
                          ) : (
                            <Typography sx={{ color: "#aaa" }}>--</Typography>
                          )}
                        </Box>
                      ))}
                    </TableCell>

                    {/* Actions - Only show for first group */}
                    {isFirstGroup && currentTab === "Active" && (
                      <TableCell
                        rowSpan={totalRows}
                        sx={{ verticalAlign: "top", p: 0 }}
                      >
                        <Box
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            px: 2,
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="contained"
                            sx={{
                              borderRadius: 1.5,
                              height: "32px",
                              bgcolor: "#1CA19B",
                              width: "120px",
                              textTransform: "none",
                              "&:hover": { bgcolor: "#158e89" },
                            }}
                            onClick={() => handleDispatchOrder(orderGroup)}
                          >
                            Dispatch
                          </Button>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );

                currentRowIndex += rowsInThisGroup;
                return row;
              });
            })}
          </TableBody>
        </Table>
      </Box>

      {/* Logout Dialog */}
      <Dialog
        open={isLogoutPopup}
        onClose={() => setIsLogoutPopup(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Logout</DialogTitle>

        <DialogContent>
          <Typography>Are you sure you want to logout?</Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setIsLogoutPopup(false)}
            variant="outlined"
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={handleLogout} variant="contained" color="error">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
